import React, { useCallback } from 'react';
import { FieldErrors } from 'react-hook-form';
import * as yup from 'yup';
import { UNREGISTERED_EMAIL_ERROR_CODE, useForgotPassword } from 'api';
import useAuthForm from 'ui/hooks/useAuthForm';
import { emailSchema } from 'utils/schemas';
import { GENERIC_ERROR_MESSAGE, UNREGISTERED_EMAIL_MESSAGE } from './constants';

interface ForgotPasswordRequestFormData {
  email: string;
}

export interface UseForgotPasswordRequestFormOptions {
  onError?: (
    errors: FieldErrors<ForgotPasswordRequestFormData> | Error,
  ) => void;
  /**
   * Called when the form is submitted
   */
  onSubmit?: () => void;
  /**
   * Called when the submission to the API returns successfully
   */
  onSuccess?: () => void;
}

type UseForgotPasswordRequestFormResult = Pick<
  ReturnType<typeof useAuthForm<ForgotPasswordRequestFormData>>,
  'formState' | 'register' | 'validationState'
> & {
  hasInputError: boolean;
  isLoading: boolean;
  onSubmit: (e: React.SyntheticEvent) => Promise<void>;
};

const schema = yup.object().shape({
  email: emailSchema.required(),
});

export default function useForgotPasswordRequestForm(
  opts?: UseForgotPasswordRequestFormOptions,
): UseForgotPasswordRequestFormResult {
  const { onError, onSubmit, onSuccess } = opts ?? {};

  const { mutate, isLoading } = useForgotPassword();

  const {
    clearErrors,
    formState,
    handleSubmit,
    hasInputError,
    register,
    setError,
    validationState,
  } = useAuthForm<ForgotPasswordRequestFormData>({
    schema,
    defaultValues: {
      email: '',
    },
  });

  const submitForm = useCallback(
    async (data: Omit<ForgotPasswordRequestFormData, 'unregisteredError'>) => {
      onSubmit?.();

      clearErrors('unregisteredError');

      mutate(data, {
        onSuccess,
        onError(err) {
          if (err.response?.data?.code === UNREGISTERED_EMAIL_ERROR_CODE) {
            const message = UNREGISTERED_EMAIL_MESSAGE;
            setError('email', { message }, { shouldFocus: true });
            onError?.(new Error(UNREGISTERED_EMAIL_MESSAGE));
          } else {
            const message = GENERIC_ERROR_MESSAGE;
            setError('unregisteredError', { message }, { shouldFocus: false });
            onError?.(new Error(message));
          }
        },
      });
    },
    [clearErrors, mutate, onError, onSubmit, onSuccess, setError],
  );

  return {
    formState,
    hasInputError,
    register,
    isLoading,
    onSubmit: handleSubmit(submitForm, onError),
    validationState,
  };
}
